import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ActivityDTO,
  ApprovalDTO,
  ClientDTO,
  CommentDTO,
  DashboardStatsDTO,
  FeedbackDTO,
  MemberDTO,
  NotificationDTO,
  OnboardingState,
  Paginated,
  ProjectDTO,
  ReviewLinkDTO,
  SessionDTO,
  TaskDTO,
  VersionDTO,
  WebsiteDTO,
  WorkspaceDTO,
} from "@markfeed/shared";
import { api, ApiError, errorMessage, setApiOrg } from "@/lib/api";

export const qk = {
  session: ["session"] as const,
  org: ["org"] as const,
  projects: (f?: object) => ["projects", f ?? {}] as const,
  project: (id: string) => ["project", id] as const,
  clients: ["clients"] as const,
  workspaces: ["workspaces"] as const,
  websites: (f?: object) => ["websites", f ?? {}] as const,
  website: (id: string) => ["website", id] as const,
  feedback: (f?: object) => ["feedback", f ?? {}] as const,
  feedbackDetail: (id: string) => ["feedback-detail", id] as const,
  tasks: (f?: object) => ["tasks", f ?? {}] as const,
  task: (id: string) => ["task", id] as const,
  members: ["members"] as const,
  notifications: ["notifications"] as const,
  dashboard: ["dashboard"] as const,
};

// ───────────── Session ─────────────

export function useSession() {
  return useQuery({
    queryKey: qk.session,
    queryFn: async (): Promise<SessionDTO | null> => {
      try {
        const s = await api<SessionDTO>("/auth/me");
        setApiOrg(s.currentOrgId);
        return s;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: (n, err) => !(err instanceof ApiError && err.status < 500) && n < 2,
  });
}

/** Store a fresh session payload (login/register/org switch) and reset tenant-scoped caches. */
export function useApplySession() {
  const qc = useQueryClient();
  return (s: SessionDTO | null) => {
    setApiOrg(s?.currentOrgId ?? null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
    qc.setQueryData(qk.session, s);
  };
}

export function useCurrentOrg() {
  const { data } = useSession();
  return data?.organizations.find((o) => o.id === data.currentOrgId) ?? null;
}

export interface OrgDetail {
  id: string;
  name: string;
  brandColor: string | null;
  timezone: string;
  role: string;
  onboarding: OnboardingState;
  plan: { code: string; name: string; limits: Record<string, number | null>; flags: Record<string, boolean> };
  usage: Record<string, number>;
}
export const useOrgDetail = () => useQuery({ queryKey: qk.org, queryFn: () => api<OrgDetail>("/orgs/current") });
export const useWorkspaces = () => useQuery({ queryKey: qk.workspaces, queryFn: () => api<WorkspaceDTO[]>("/workspaces") });

// ───────────── Generic mutation helper ─────────────

export function useApiMutation<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: { invalidate?: QueryKey[]; success?: string | ((r: TResult) => string); onSuccess?: (r: TResult, v: TVars) => void; silentError?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, v) => {
      for (const key of opts.invalidate ?? []) void qc.invalidateQueries({ queryKey: key });
      if (opts.success) toast.success(typeof opts.success === "function" ? opts.success(r) : opts.success);
      opts.onSuccess?.(r, v);
    },
    onError: (err) => {
      if (!opts.silentError) toast.error(errorMessage(err));
    },
  });
}

// ───────────── Domain queries ─────────────

export const useProjects = (filters: { search?: string; status?: string; workspaceId?: string } = {}) =>
  useQuery({ queryKey: qk.projects(filters), queryFn: () => api<ProjectDTO[]>("/projects", { query: filters }), placeholderData: keepPreviousData });
export const useProject = (id: string) =>
  useQuery({ queryKey: qk.project(id), queryFn: () => api<ProjectDTO>(`/projects/${id}`), enabled: !!id });
export const useClients = () => useQuery({ queryKey: qk.clients, queryFn: () => api<ClientDTO[]>("/clients") });
export const useWebsites = (filters: { projectId?: string } = {}) =>
  useQuery({ queryKey: qk.websites(filters), queryFn: () => api<WebsiteDTO[]>("/websites", { query: filters }) });
export const useWebsite = (id: string) =>
  useQuery({ queryKey: qk.website(id), queryFn: () => api<WebsiteDTO>(`/websites/${id}`), enabled: !!id });
export const useVersions = (websiteId: string) =>
  useQuery({ queryKey: ["versions", websiteId], queryFn: () => api<VersionDTO[]>(`/websites/${websiteId}/versions`), enabled: !!websiteId });
export const useReviewLinks = (websiteId: string) =>
  useQuery({ queryKey: ["review-links", websiteId], queryFn: () => api<ReviewLinkDTO[]>(`/websites/${websiteId}/review-links`), enabled: !!websiteId });
export const useApprovals = (filters: { websiteId?: string; projectId?: string }) =>
  useQuery({ queryKey: ["approvals", filters], queryFn: () => api<ApprovalDTO[]>("/approvals", { query: filters }) });

export interface FeedbackFilters {
  projectId?: string;
  websiteId?: string;
  status?: string;
  priority?: string;
  assignee?: string;
  search?: string;
  order?: string;
  page?: number;
  pageSize?: number;
}
export const useFeedbackList = (filters: FeedbackFilters) =>
  useQuery({
    queryKey: qk.feedback(filters),
    queryFn: () => api<Paginated<FeedbackDTO>>("/feedback", { query: { ...filters } }),
    placeholderData: keepPreviousData,
  });
export const useFeedbackDetail = (id: string | null) =>
  useQuery({
    queryKey: qk.feedbackDetail(id ?? ""),
    queryFn: () => api<{ feedback: FeedbackDTO; comments: CommentDTO[]; activity: ActivityDTO[] }>(`/feedback/${id}`),
    enabled: !!id,
  });

export const useTasks = (filters: { projectId?: string; assignee?: string; priority?: string } = {}) =>
  useQuery({ queryKey: qk.tasks(filters), queryFn: () => api<TaskDTO[]>("/tasks", { query: filters }) });
export const useTaskDetail = (id: string | null) =>
  useQuery({
    queryKey: qk.task(id ?? ""),
    queryFn: () => api<{ task: TaskDTO; comments: CommentDTO[]; activity: ActivityDTO[] }>(`/tasks/${id}`),
    enabled: !!id,
  });

export const useMembers = () => useQuery({ queryKey: qk.members, queryFn: () => api<MemberDTO[]>("/team/members") });
export const useNotifications = () =>
  useQuery({
    queryKey: qk.notifications,
    queryFn: () => api<{ items: NotificationDTO[]; unread: number }>("/notifications"),
    refetchInterval: 30_000,
  });
export const useDashboard = () => useQuery({ queryKey: qk.dashboard, queryFn: () => api<DashboardStatsDTO>("/dashboard") });

/** Invalidate everything that shows feedback/task state. */
export const FEEDBACK_KEYS: QueryKey[] = [["feedback"], ["feedback-detail"], ["tasks"], ["task"], ["dashboard"], ["projects"], ["project"], ["websites"], ["website"], ["notifications"]];
